// UsageHistory Component
// Displays usage events in a table format with filtering

import React, { useState } from 'react';
import { FiFilter, FiCalendar } from 'react-icons/fi';
import type { UsageEvent } from '../../../api/billing';
import styles from './UsageHistory.module.css';

interface UsageHistoryProps {
  events: UsageEvent[];
  loading?: boolean;
  onFilterChange?: (service: string) => void;
}

export function UsageHistory({ events, loading = false, onFilterChange }: UsageHistoryProps) {
  const [selectedService, setSelectedService] = useState<string>('all');

  // Get unique services from events (derive from event_type)
  const services = ['all', ...Array.from(new Set(events.map(e => e.event_type).filter(Boolean)))];

  const handleServiceChange = (service: string) => {
    setSelectedService(service);
    if (onFilterChange) {
      onFilterChange(service === 'all' ? '' : service);
    }
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getServiceColor = (eventType: string) => {
    const colors: Record<string, string> = {
      monitoring: 'var(--color-info)',
      briefing: 'var(--color-accent)',
      similarity: 'var(--color-secondary)',
      similarity_check: 'var(--color-secondary)',
      analytics: 'var(--color-success)',
      api_call: 'var(--color-warning)',
    };
    return colors[eventType.toLowerCase()] || 'var(--color-slate-500)';
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <p>Loading usage history...</p>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className={styles.empty}>
        <FiCalendar className={styles.emptyIcon} />
        <p>No usage events yet</p>
        <span className={styles.emptyNote}>
          Your usage activity will appear here
        </span>
      </div>
    );
  }

  return (
    <div className={styles.usageHistory}>
      {/* Filter Bar */}
      <div className={styles.filterBar}>
        <div className={styles.filterLabel}>
          <FiFilter />
          <span>Filter by service:</span>
        </div>
        <div className={styles.filterButtons}>
          {services.map(service => (
            <button
              key={service}
              className={`${styles.filterButton} ${selectedService === service ? styles.active : ''}`}
              onClick={() => handleServiceChange(service)}
            >
              {service ? service.charAt(0).toUpperCase() + service.slice(1).replace(/_/g, ' ') : 'Unknown'}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Date & Time</th>
              <th>Service</th>
              <th>Action</th>
              <th>Credits</th>
            </tr>
          </thead>
          <tbody>
            {events.map(event => (
              <tr key={event.id}>
                <td className={styles.dateCell}>
                  <FiCalendar className={styles.dateIcon} />
                  {formatDate(event.timestamp)}
                </td>
                <td>
                  <span
                    className={styles.serviceBadge}
                    style={{ '--service-color': getServiceColor(event.event_type || '') } as React.CSSProperties}
                  >
                    {event.event_type ? event.event_type.replace(/_/g, ' ') : 'Unknown'}
                  </span>
                </td>
                <td className={styles.actionCell}>
                  {event.metadata?.status || 'completed'}
                </td>
                <td className={styles.creditsCell}>
                  <span className={styles.credits}>-{event.quantity}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

